/**
 * Utility class for client-side image compression
 */
export class ImageCompressor {
    constructor(config = {}) {
        this.config = {
            maxWidth: config.maxWidth || 1280,
            quality: config.quality || 0.9,
            ...config
        };
    }

    /**
     * Compress an image file
     * @param {File} file - The image file to compress
     * @returns {Promise<Blob>} - Compressed image as a Blob
     */
    async compress(file) {
        // Create image element
        const img = await this.createImage(file);
        
        // Calculate dimensions
        const { width, height } = this.calculateDimensions(img);
        
        // Create canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob
        return new Promise((resolve) => {
            canvas.toBlob(
                (blob) => resolve(blob),
                file.type,
                this.config.quality
            );
        });
    }

    /**
     * Create an image element from a file
     * @private
     */
    createImage(file) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = URL.createObjectURL(file);
        });
    }

    /**
     * Calculate new dimensions maintaining aspect ratio
     * @private
     */
    calculateDimensions(img) {
        let { width, height } = img;
        
        if (width > this.config.maxWidth) {
            height = Math.round((height * this.config.maxWidth) / width);
            width = this.config.maxWidth;
        }
        
        return { width, height };
    }
}
